/**
 * A local copy of the map editor's unsaved work, so a closed tab, a reload or a
 * crashed browser does not throw away an hour of edits.
 *
 * The editor holds edits in memory until Save. This keeps the same edits in the
 * browser's localStorage as they are made, and the editor offers to restore them
 * the next time the team's map is opened. It is not an autosave: nothing reaches
 * the server until the user saves, which is the editor's whole contract (Save and
 * Cancel), and the app may be editing the same pins meanwhile.
 *
 * A draft stores the map as it was LOADED next to the map as it was EDITED, so a
 * restore can replay only the user's own changes onto what the server holds now
 * (see {@link rebaseDraft}). Restoring the edited map as-is would delete every pin
 * a teammate added since, because a save diffs the editor's map against the
 * server's and reads a missing pin as a deleted one.
 */

const KEY_PREFIX = 'wh-map-draft:';
const VERSION = 1;
/** A draft older than this is dropped: the map it was made against is long gone. */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** The editor's layers a draft carries. Each is a list of records with an id. */
export const DRAFT_LAYERS = ['borders', 'subAreas', 'forbiddenAreas', 'objects', 'lines', 'pinFolders'] as const;
type Layer = (typeof DRAFT_LAYERS)[number];

export type DraftableMap = { name?: string } & Partial<Record<Layer, { id: string }[]>>;

export type MapEditorDraft<T extends DraftableMap> = {
  savedAt: number;
  base: T;
  state: T;
};

/**
 * What survives a trip through storage. A picture staged for upload is a `File`,
 * which cannot be stored, so it is dropped; a stored picture (an id and its URL)
 * is kept.
 */
function storable<T extends DraftableMap>(map: T): T {
  return JSON.parse(
    JSON.stringify(map, (key, value) => {
      if (key === 'photos' && Array.isArray(value)) {
        return value.filter((p) => p && typeof p === 'object' && 'id' in p);
      }
      return value;
    }),
  ) as T;
}

export function saveDraft<T extends DraftableMap>(teamId: string, base: T, state: T, now = Date.now()): void {
  try {
    const draft = { v: VERSION, savedAt: now, base: storable(base), state: storable(state) };
    window.localStorage.setItem(KEY_PREFIX + teamId, JSON.stringify(draft));
  } catch {
    // Storage full or blocked (a private window, a large map). The editor still
    // works; it only loses the safety net, which is no worse than before.
  }
}

export function loadDraft<T extends DraftableMap>(teamId: string, now = Date.now()): MapEditorDraft<T> | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + teamId);
    if (!raw) return null;
    const d = JSON.parse(raw) as { v?: number; savedAt?: number; base?: T; state?: T };
    if (d.v !== VERSION || typeof d.savedAt !== 'number' || !d.base || !d.state || now - d.savedAt > DRAFT_MAX_AGE_MS) {
      clearDraft(teamId);
      return null;
    }
    return { savedAt: d.savedAt, base: d.base, state: d.state };
  } catch {
    return null;
  }
}

export function clearDraft(teamId: string): void {
  try {
    window.localStorage.removeItem(KEY_PREFIX + teamId);
  } catch {
    // Nothing to do: the draft was never readable either.
  }
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Replays one layer's draft changes onto the current records, by id:
 * - a record the user added is added;
 * - a record the user deleted is deleted;
 * - a record the user edited replaces the current one, if it still exists;
 * - everything else is the current record, so changes made elsewhere since the
 *   draft (a teammate's new pin, an edit in the app) are kept.
 */
function rebaseLayer<R extends { id: string }>(current: R[], base: R[], draft: R[]): R[] {
  const baseById = new Map(base.map((r) => [r.id, r]));
  const draftById = new Map(draft.map((r) => [r.id, r]));
  const out: R[] = [];
  for (const c of current) {
    const b = baseById.get(c.id);
    if (!b) {
      out.push(c);
      continue;
    }
    const d = draftById.get(c.id);
    if (!d) continue;
    out.push(same(d, b) ? c : d);
  }
  for (const d of draft) if (!baseById.has(d.id)) out.push(d);
  return out;
}

/** The current map with the draft's own changes applied on top. */
export function rebaseDraft<T extends DraftableMap>(current: T, draft: MapEditorDraft<T>): T {
  const out: T = { ...current };
  if (draft.state.name !== draft.base.name) out.name = draft.state.name;
  for (const layer of DRAFT_LAYERS) {
    const c = current[layer];
    const b = draft.base[layer] ?? [];
    const d = draft.state[layer] ?? [];
    if (!c && d.length === 0) continue;
    (out as DraftableMap)[layer] = rebaseLayer(c ?? [], b, d);
  }
  return out;
}

/** True when a draft would change nothing about the current map. */
export function draftIsEmpty<T extends DraftableMap>(current: T, draft: MapEditorDraft<T>): boolean {
  const rebased = rebaseDraft(storable(current), draft);
  return same(storable(rebased), storable(current));
}
